// hooks/useUserAgreements.js
import { useState, useEffect, useCallback } from "react";
import apiClient from "utils/apiClient";

const BASE_URL = "profile-service/api/v1/user-agreements";

const useUserAgreements = (page = 0, pageSize = 10) => {
  const authToken = sessionStorage.getItem("authToken");
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeAgreement, setActiveAgreement] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  const applyAgreementResponse = useCallback((data) => {
    if (data && data.content && Array.isArray(data.content)) {
      setAgreements(data.content);
      setTotalPages(data.totalPages || 1);
      setTotalElements(data.totalElements || 0);
      return;
    }

    if (Array.isArray(data)) {
      setAgreements(data);
      setTotalPages(1);
      setTotalElements(data.length);
      return;
    }

    setAgreements([]);
    setTotalPages(0);
    setTotalElements(0);
  }, []);

  const fetchAgreements = useCallback(async () => {
    if (!authToken) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(BASE_URL, {
        params: { page, size: pageSize },
      });
      applyAgreementResponse(response.data);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      setAgreements([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [applyAgreementResponse, authToken, page, pageSize]);

  const searchAgreements = useCallback(
    async (query = "", status = "ALL", searchPage = 0) => {
      if (!authToken) return;

      try {
        setLoading(true);
        setError(null);

        const trimmedQuery = String(query || "").trim();
        const params = {
          page: searchPage,
          size: pageSize,
        };

        if (trimmedQuery.length >= 3) {
          params.q = trimmedQuery;
        }
        if (status && status !== "ALL") {
          params.status = status;
        }

        const isFiltered = Boolean(params.q) || Boolean(params.status);
        const endpoint = isFiltered ? `${BASE_URL}/search` : BASE_URL;
        const response = await apiClient.get(endpoint, { params });
        applyAgreementResponse(response.data);
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        setError(errorMessage);
        setAgreements([]);
        setTotalPages(0);
        setTotalElements(0);
      } finally {
        setLoading(false);
      }
    },
    [applyAgreementResponse, authToken, pageSize]
  );

  const updateAgreementStatus = useCallback(
    async (id, newStatus) => {
      if (!id || !newStatus) {
        throw new Error("Agreement ID and new status are required for status update.");
      }
      try {
        const url = `profile-service/api/v1/user-agreements/${id}/status`;
        const response = await apiClient.patch(url, { 
            status: newStatus 
        }); 
        // Refresh will be called automatically via useEffect when page/pageSize changes
        await fetchAgreements(); 
        return response.data;
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        setError(errorMessage);
        throw err;
      }
    },
    [fetchAgreements]
  );

  const addAgreement = useCallback(
    async (agreementData) => {
      try {
        const response = await apiClient.post(BASE_URL, agreementData);
        // Refresh will be called automatically via useEffect when page/pageSize changes
        await fetchAgreements();
        return response.data;
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        setError(errorMessage);
        throw err;
      }
    },
    [fetchAgreements]
  );

  const updateAgreement = useCallback(
    async (id, agreementData) => {
      if (!id) {
        throw new Error("Agreement ID is required for update.");
      }
      try {
        const url = `${BASE_URL}/${id}`;
        const response = await apiClient.put(url, agreementData);
        // Refresh will be called automatically via useEffect when page/pageSize changes
        await fetchAgreements();
        return response.data;
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        setError(errorMessage);
        throw err;
      }
    },
    [fetchAgreements]
  );

  const deleteAgreement = useCallback(
    async (id) => {
      if (!id) {
        return;
      }
      try {
        const url = `${BASE_URL}/${id}`;
        await apiClient.delete(url);
        // Refresh will be called automatically via useEffect when page/pageSize changes
        await fetchAgreements();
        return true;
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message;
        setError(errorMessage);
        throw err;
      }
    },
    [fetchAgreements]
  );

  const getAgreementById = useCallback(async (id) => {
    if (!id) {
      throw new Error("Agreement ID is required.");
    }
    try {
      const url = `${BASE_URL}/${id}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      throw err;
    }
  }, []);

  // Auto-fetch when page or pageSize changes
  useEffect(() => {
    if (authToken) {
      fetchAgreements();
    }
  }, [authToken, fetchAgreements]);

  const getActiveAgreement = useCallback(async () => {
  setLoading(true);
  setError(null);

  try {
    const url = `${BASE_URL}/active`;
    const response = await apiClient.get(url);
    const data = response.data;

    if (data?.content && Array.isArray(data.content)) {
      setActiveAgreement(data.content);
    }
    else if (Array.isArray(data)) {
      setActiveAgreement(data);
    }
    else if (data && typeof data === "object") {
      setActiveAgreement([data]); // Normalize into array
    }
    else {
      setActiveAgreement([]);
    }
  } catch (err) {
    const errorMessage = err.response?.data?.message || err.message;
    setError(errorMessage);
  } finally {
    setLoading(false);
  }
}, []);


  return {
    agreements,
    loading,
    error,
    totalPages,
    totalElements,
    refetch: fetchAgreements,
    searchAgreements,
    addAgreement,
    activeAgreement,
    getActiveAgreement,
    updateAgreementStatus,
    updateAgreement,
    deleteAgreement,
    getAgreementById,
  };
};

export default useUserAgreements;
